import { useCallback, useEffect, useState } from "react";
import { useNotification } from "@/context/NotificationContext";
import { fetchRecurringEmailSubscriptions } from "@/api/fetchRecurringEmailSubscriptions/fetchRecurringEmailSubscriptionsApi";
import { RecurringEmailSubscription } from "@/api/fetchRecurringEmailSubscriptions/types";
import { updateRecurringEmailSubscription } from "@/api/updateRecurringEmailSubscription/updateRecurringEmailSubscriptionApi";

export function useRecurringEmailSubscriptions(employeeUuid?: string) {
  const { showNotification } = useNotification();

  const [subscriptions, setSubscriptions] = useState<
    RecurringEmailSubscription[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [updatingRecurringEmail, setUpdatingRecurringEmail] = useState<
    string | null
  >(null);

  useEffect(() => {
    const loadSubscriptions = async () => {
      if (!employeeUuid) return;

      try {
        setLoading(true);
        const response = await fetchRecurringEmailSubscriptions(employeeUuid);
        setSubscriptions(response.data.recurringEmails);
      } catch {
        // Error handled by axios interceptor
      } finally {
        setLoading(false);
      }
    };

    loadSubscriptions();
  }, [employeeUuid]);

  // Writes on flip, independent of the form's Save Changes button — matching
  // the Permission Overrides block above it on the Edit User screen.
  const toggleSubscription = useCallback(
    async (recurringEmail: string, isSubscribed: boolean) => {
      if (!employeeUuid) return;

      try {
        setUpdatingRecurringEmail(recurringEmail);
        const response = await updateRecurringEmailSubscription(
          employeeUuid,
          recurringEmail,
          { isSubscribed },
        );
        const updated = response.data.recurringEmail;

        setSubscriptions((prev) =>
          prev.map((subscription) =>
            subscription.recurringEmail === updated.recurringEmail
              ? updated
              : subscription,
          ),
        );

        showNotification(
          updated.isSubscribed
            ? `Subscribed to ${updated.label}`
            : `Unsubscribed from ${updated.label}`,
          "",
          "success",
        );
      } catch {
        // Error handled by axios interceptor; the toggle keeps its stored state
      } finally {
        setUpdatingRecurringEmail(null);
      }
    },
    [employeeUuid, showNotification],
  );

  return {
    subscriptions,
    loading,
    updatingRecurringEmail,
    toggleSubscription,
  };
}
